import { Region, SettingKeyType } from '@crew/enums/app'
import { useTranslation } from '@crew/modules/i18n'
import { useUserSetting } from '@crew/states'
import {
  ColumnDef,
  RowSelectionState,
  TableOptions,
  getCoreRowModel,
} from '@tanstack/react-table'
import { CrewAvatarSize } from 'components/elements/crewAvatar/crewAvatar'
import { CrewUserAvatar } from 'components/elements/crewUserAvatar/crewUserAvatar'
import { FC, memo, useMemo, useState } from 'react'
import {
  SuggestionItem,
  useTaskDetailRelatedInfoListContext,
} from '../../../../taskDetailRelatedInfoList'
import {
  CrewTable,
  SelectCheckbox,
} from 'components/elements/crewTable/crewTable'
import { useValueChangeEffect } from '@crew/hooks'
import { CrewEntityIcon } from 'components/elements/crewEntityIcon/crewEntityIcon'

// renderとして使うのでmemo不可
// 更新日時
const UpdateAt: FC<{ date: string }> = (props) => {
  const { t } = useTranslation()

  // ユーザー設定からデフォルトのユーザープロファイル地域を取得
  const defaultUserProfileRegion = useUserSetting(
    SettingKeyType.UserProfileRegion,
    Region.Japan.value
  )

  return (
    <>
      {props
        ? t('format.timestamp', {
            value: props.date,
            timeZone: defaultUserProfileRegion,
          })
        : undefined}
    </>
  )
}

export const SuggestionTable = memo(() => {
  const { t } = useTranslation()

  // 表示状態、関連提案情報の選択状態更新、生成された関連提案情報を取得
  const { displayState, setSelectedSuggestions, relatedSuggestions } =
    useTaskDetailRelatedInfoListContext()

  // 行選択状態を更新する
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({})
  useValueChangeEffect(
    () => {
      if (!relatedSuggestions) {
        setSelectedSuggestions([])
      } else {
        setSelectedSuggestions(
          relatedSuggestions.filter((suggestion) => {
            return rowSelection[suggestion.id]
          })
        )
      }
    },
    [relatedSuggestions, rowSelection, setSelectedSuggestions],
    rowSelection
  )

  const columns = useMemo<ColumnDef<SuggestionItem>[]>(
    () => [
      // 行選択チェックボックス
      {
        id: 'select',
        accessorKey: 'select',
        header: ({ table }) => (
          <SelectCheckbox
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div className="flex flex-1 justify-center">
            <SelectCheckbox
              {...{
                checked: row.getIsSelected(),
                disabled: !row.getCanSelect(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
        size: 80,
        minSize: 50,
        enableSorting: false,
        meta: {
          align: 'center',
        },
      },
      // 関連先
      {
        id: 'title',
        accessorKey: 'title',
        header: () => t('label.relatedItem'),
        cell: ({ row }) => (
          <div className="flex flex-row gap-1 items-center min-w-0">
            <CrewEntityIcon
              entityType={row.original.entityType}
              className="flex-shrink-0"
            />
            <div className="min-w-0 truncate">{row.original.title}</div>
          </div>
        ),
        size: 500,
        minSize: 50,
        enableSorting: false,
      },
      // 更新者
      {
        id: 'updatedBy',
        accessorKey: 'updatedBy',
        header: t('label.updatedBy'),
        cell: ({ row }) => (
          <div className="truncate">
            <CrewUserAvatar
              userId={row.original.updatedBy.id}
              displayName={row.original.updatedBy.displayName}
              cacheValue={
                row.original.updatedBy.id + row.original.updatedBy.version
              }
              size={CrewAvatarSize.xs}
              showLabel={true}
              disableClick={true}
            />
          </div>
        ),
        size: 140,
        minSize: 120,
        enableSorting: false,
      },
      // 更新日時
      {
        id: 'updatedAt',
        accessorKey: 'updatedAt',
        header: t('label.updateDatetime'),
        cell: ({ row }) => (
          <div className="truncate">
            <UpdateAt date={row.original.updatedAt} />
          </div>
        ),
        size: 160,
        minSize: 50,
        enableSorting: false,
      },
    ],
    [t]
  )

  const tableOptions: TableOptions<SuggestionItem> = {
    data: relatedSuggestions,
    columns,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    state: {
      rowSelection,
    },
    getRowId: (row) => row.id,
    onRowSelectionChange: setRowSelection,
    meta: {
      headerRowHeight: 35,
      dataRowHeight: 40,
    },
  }

  // 表示状態が関連提案でない場合は何も表示しない
  if (displayState !== 'suggestion') {
    return null
  }

  return (
    <>
      {/* 関連提案テーブル */}
      <CrewTable tableOptions={tableOptions} showPager={false} />
    </>
  )
})
